<script setup>
import {computed, ref, watch} from 'vue';
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import ButtonGroup from "primevue/buttongroup";
import { useServerTable } from '@/composables/useServerTable.js';
import { useAttachmentStore } from '@/stores/attachment.js';
import DocumentForm from '@/components/documents/DocumentForm.vue';
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";
import { useToast } from "primevue/usetoast";

const props = defineProps({
  target: { type: String, required: true },
  entityId: { type: [Number, String], required: true },
});

const store = useAttachmentStore();
const toast = useToast();

const showAddDialog = ref(false);
const showConfirmDeleteDialog = ref(false);
const fileToDelete = ref(null);
const availableFileTypes = ref([]);

const {
  data: files,
  totalRecords,
  loading,
  rowsPerPage,
  onPageChange,
  setSearch,
  setExtra,
  firstIndex,
  load,
} = useServerTable({
  initialRows: 5,
  debounceMs: 500,
  loadPage: params => store.loadFiles(params),
});

watch(
    () => [props.target, props.entityId],
    async ([target, id]) => {
      if (target && id != null) {
        setExtra({ target, id });
        // Загружаем доступные типы файлов
        try {
          availableFileTypes.value = await store.fetchDocumentTypes({ target, kind: 'file' });
        } catch (e) {
          availableFileTypes.value = [];
        }
      }
    },
    { immediate: true }
);

const showControls = computed(() => loading.value || totalRecords.value > 0);
const canAddFiles = computed(() => availableFileTypes.value.length > 0);

function downloadFile(file) {
  if (!file || !file.download_url) return;
  const link = document.createElement('a');
  link.href = file.download_url;
  link.setAttribute('download', file.file_name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function openDeleteDialog(file) {
  fileToDelete.value = file;
  showConfirmDeleteDialog.value = true;
}

async function handleConfirmDelete() {
  if (!fileToDelete.value) return;
  try {
    await store.deleteAttachment(fileToDelete.value.id);
    toast.add({ severity: 'success', summary: 'Успех', detail: 'Файл удален', life: 2500 });
    await load();
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить файл', life: 4000 });
  } finally {
    showConfirmDeleteDialog.value = false;
    fileToDelete.value = null;
  }
}
</script>

<template>
  <div>
    <Card>
      <template #title>
        <div class="w-full flex items-center justify-between px-4 gap-3 text-lg">
          <span>Файлы</span>
          <div class="flex items-center gap-2">
            <IconField v-if="showControls">
              <InputIcon class="pi pi-search" />
              <InputText @update:modelValue="setSearch" placeholder="Поиск файлов..." size="small" class="h-7"/>
            </IconField>
            <Button v-if="canAddFiles" icon="pi pi-plus" rounded text @click="showAddDialog = true" size="small"/>
          </div>
        </div>
      </template>
      <template #content>
        <DataTable
            :value="files"
            :paginator="totalRecords > rowsPerPage"
            :rows="rowsPerPage"
            :totalRecords="totalRecords"
            :loading="loading"
            @page="onPageChange"
            :first="firstIndex()"
            lazy
            responsive-layout="scroll"
            paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
            currentPageReportTemplate="от {first} до {last} из {totalRecords}"
            emptyMessage="Файлов нет"
            class="text-sm"
        >
          <Column field="file_name">
            <template #body="{ data }">
              {{ data.file_name }}
            </template>
          </Column>
          <Column style="width: 80px;">
            <template #body="{ data }">
              <ButtonGroup>
                <Button
                    icon="pi pi-download"
                    size="small"
                    severity="secondary"
                    rounded
                    text
                    @click="downloadFile(data)"
                />
                <Button
                    icon="pi pi-trash"
                    size="small"
                    severity="warn"
                    rounded
                    text
                    @click="openDeleteDialog(data)"
                />
              </ButtonGroup>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <DocumentForm
        v-model:visible="showAddDialog"
        :target="props.target"
        :entity-id="props.entityId"
        kind="file"
        @saved="load"
    />

    <ConfirmDeleteDialog
        v-model:visible="showConfirmDeleteDialog"
        :documentName="fileToDelete?.file_name"
        @confirm="handleConfirmDelete"
    />
  </div>
</template>

<style scoped>
:deep(.p-datatable-thead > tr > th),
:deep(.p-datatable-tbody > tr > td) {
  border: none !important;
  background: transparent !important;
  border-bottom: none !important;
}
:deep(.p-datatable-tbody > tr > td) {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}
:deep(.p-datatable-thead > tr > th) {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}
:deep(.p-paginator .p-paginator-prev),
:deep(.p-paginator .p-paginator-next) {
  height: 2rem;
  min-width: 2rem;
}
:deep(.p-card-body) {
  padding: 2px 0 8px;
}
</style>
